/**
 * How a team is drawn on the leaderboard: as its country's flag, or as its kit
 * (specs/champions-league AC-3.6).
 *
 * The World Cup draws flags — one nation, one flag, unambiguous. A club competition cannot:
 * `CountryFlag` resolves a club's country too, so Real Madrid and Barcelona both render the
 * Spanish flag and a live chip shows the same mark twice. Clubs are drawn as kits, which is
 * what AC-3.3 asked for and what every other competition surface already does.
 */
export type LeaderboardTeamVisual = 'flag' | 'kit';

/** Icon row height: SoccerKit size=34, aligns with compact flag row. */
export const LEADERBOARD_WINNER_SCORER_ICON_ROW_HEIGHT = 34;

/** Viewport width at or below this uses a narrower rank column and smaller medals / rank number. */
export const LEADERBOARD_RANK_COMPACT_MAX_WIDTH = 390;

export const LEADERBOARD_CARD_COPY = {
  EMPTY_PLACEHOLDER: '—',
  /** Caption shown under each pick tile while peers' futures are withheld pre-kickoff. */
  LOCKED_PLACEHOLDER: 'Hidden',
  LOCKED_A11Y: 'Prediction hidden until the first match kicks off',
  EXACT_HITS_A11Y: 'Exact hits',
  LIVE_BADGE: 'LIVE',
  /** Overflow marker on a capped live strip (specs/champions-league AC-18.1). */
  liveMoreGames: (count: number) => `+${count} more`,
} as const;

/**
 * How many live-match chips one member's row will draw before collapsing the rest into a
 * count (specs/champions-league AC-18.1).
 *
 * The strip was written when the World Cup was the only tournament, and the World Cup plays at
 * most TWO matches at once — so "one chip per live match" was implicitly bounded at two and
 * never needed a cap. The Champions League breaks that: an ordinary matchday has 9 in play
 * together (two early fixtures still running when seven more kick off) and the final
 * league-phase matchday starts all 18 at once. Each chip draws two team kits and a x2 glyph, so
 * an uncapped strip on a 30-member squad is 270 chips, and 540 on matchday 8.
 *
 * Two, so the World Cup never truncates and is visually untouched (AC-18.3).
 */
export const LEADERBOARD_LIVE_GAMES_VISIBLE = 2;

/** Accessibility labels for the leaderboard row. */
export const LEADERBOARD_CARD_A11Y = {
  optionsForUser: (name: string) => `Options for ${name}`,
  livePrediction: (params: {
    homeTeam: string;
    awayTeam: string;
    predictionHome: string | number;
    predictionAway: string | number;
    outcome: string;
    pointsEarned: number;
    isDoublePoints: boolean;
  }) =>
    `${params.homeTeam} ${params.predictionHome} - ${params.predictionAway} ${params.awayTeam}, ${params.outcome}, +${params.pointsEarned} points${
      params.isDoublePoints ? ' double' : ''
    }`,
} as const;

/**
 * Test ids for the live strip, so the cap is assertable rather than eyeballed.
 * `userId` is optional on a leaderboard entry (a row can predate a linked account), so these
 * take it as such rather than forcing a cast at every call site.
 */
export const LEADERBOARD_CARD_TEST_IDS = {
  liveStrip: (userId: string | undefined) => `leaderboard-live-strip-${userId ?? 'unknown'}`,
  liveMore: (userId: string | undefined) => `leaderboard-live-more-${userId ?? 'unknown'}`,
} as const;

export const LEADERBOARD_RANK_COMPACT = {
  RANK_COL_WIDTH: 28,
  NUMBER_CIRCLE: 24,
  NUMBER_FONT: 10,
  MEDAL_GOLD: 18,
  MEDAL_SILVER: 16,
  MEDAL_BRONZE: 16,
} as const;

/** Shared column metrics: user column flexes; winner → stats → points packed toward the right. */
export const LEADERBOARD_CARD_LAYOUT = {
  /** Gap between rank | user | (winner+stats+pts cluster). */
  MAIN_ROW_GAP: 6,
  /** No gap between columns inside the right cluster — they sit flush. */
  RIGHT_CLUSTER_GAP: 0,
  /** Slight right shift so stats sit closer to points. */
  STATS_LEADING_MARGIN: 6,
  RANK_WIDTH: 36,
  /** Tighter: flag slot (36) + gap (2) + kit slot (38) = 76. */
  WINNER_SCORER_WIDTH: 76,
  FLAG_SLOT_WIDTH: 36,
  KIT_SLOT_WIDTH: 38,
  ICON_ROW_GAP: 2,
  /** Flag + kit row height (captions sit tight underneath). */
  WINNER_SCORER_ICON_ROW_HEIGHT: LEADERBOARD_WINNER_SCORER_ICON_ROW_HEIGHT,
  STATS_WIDTH: 36,
  PTS_WIDTH: 40,
} as const;
